import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, AlertTriangle, Info, X, XCircle } from "lucide-react";

export type ToastTone = "success" | "error" | "info" | "warning";

export interface ToastInput {
  message: ReactNode;
  tone?: ToastTone;
  /** Milliseconds before the toast auto-dismisses. 0 means "sticky". Default 4000. */
  durationMs?: number;
  /** Optional title shown above the message. */
  title?: ReactNode;
}

interface ActiveToast extends Required<Omit<ToastInput, "title" | "message">> {
  id: number;
  message: ReactNode;
  title?: ReactNode;
}

interface ToastContextValue {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
  success: (message: ReactNode, opts?: { title?: ReactNode; durationMs?: number }) => number;
  error: (message: ReactNode, opts?: { title?: ReactNode; durationMs?: number }) => number;
  info: (message: ReactNode, opts?: { title?: ReactNode; durationMs?: number }) => number;
  warning: (message: ReactNode, opts?: { title?: ReactNode; durationMs?: number }) => number;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Sprint polish: a single, dependency-free toast system used by every
 * CRUD action across the ERP. The provider is mounted once near the
 * app root; consumers call `useToast().success("...")` etc.
 *
 * Design choices:
 * - Stack-based: a new toast pushes the existing ones down, never
 *   replaces them. Users always see the result of the action they
 *   just took.
 * - Auto-dismiss: defaults to 4 s, but error toasts default to 6 s
 *   (longer so the user can actually read the message).
 * - Polite by default: `aria-live="polite"` so screen readers
 *   announce toasts after the current speech, never interrupting.
 * - Reachable: every toast has a visible close button (also
 *   presses Esc).
 * - Tail-free: no third-party library. One small component.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef<Map<number, number>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const handle = timersRef.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback((toast: ToastInput) => {
    idRef.current += 1;
    const id = idRef.current;
    const tone: ToastTone = toast.tone ?? "info";
    const durationMs = toast.durationMs ?? (tone === "error" ? 6000 : 4000);
    setToasts((current) => [...current, { id, message: toast.message, title: toast.title, tone, durationMs }]);
    if (durationMs > 0) {
      const handle = window.setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, handle);
    }
    return id;
  }, [dismiss]);

  useEffect(() => () => {
    timersRef.current.forEach((handle) => window.clearTimeout(handle));
    timersRef.current.clear();
  }, []);

  // Esc closes the most recent toast — gives keyboard users a way
  // out without having to aim for the X.
  useEffect(() => {
    if (toasts.length === 0) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const last = toasts[toasts.length - 1];
        if (last) dismiss(last.id);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [toasts, dismiss]);

  const value = useMemo<ToastContextValue>(() => {
    const byTone = (tone: ToastTone) => (message: ReactNode, opts?: { title?: ReactNode; durationMs?: number }) =>
      show({ message, tone, title: opts?.title, durationMs: opts?.durationMs });
    return {
      show,
      dismiss,
      success: byTone("success"),
      error: byTone("error"),
      info: byTone("info"),
      warning: byTone("warning"),
    };
  }, [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          zIndex: 9999,
          maxWidth: "min(420px, calc(100vw - 32px))",
          pointerEvents: "none",
        }}
      >
        {toasts.map((toast) => (
          <ToastView key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONE_STYLES: Record<ToastTone, { bg: string; fg: string; border: string; Icon: typeof CheckCircle2 }> = {
  success: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0", Icon: CheckCircle2 },
  error: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca", Icon: XCircle },
  info: { bg: "#f0fdf4", fg: "#15803d", border: "#bbf7d0", Icon: Info },
  warning: { bg: "#fffbeb", fg: "#b45309", border: "#fde68a", Icon: AlertTriangle },
};

function ToastView({ toast, onDismiss }: { toast: ActiveToast; onDismiss: () => void }) {
  const style = TONE_STYLES[toast.tone];
  const Icon = style.Icon;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        background: style.bg,
        color: style.fg,
        border: `1px solid ${style.border}`,
        borderRadius: 10,
        boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
        pointerEvents: "auto",
        fontSize: 14,
        lineHeight: 1.4,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <Icon size={20} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && <strong style={{ display: "block", marginBottom: 2 }}>{toast.title}</strong>}
        <span style={{ display: "block", wordBreak: "break-word" }}>{toast.message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Ёпиш"
        style={{
          background: "transparent",
          border: 0,
          padding: 0,
          marginLeft: 4,
          color: "inherit",
          cursor: "pointer",
          lineHeight: 0,
        }}
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast() must be used inside <ToastProvider>");
  }
  return ctx;
}
